// Date windows for the analytics queries. Pure; tested in window.test.ts.
//
// A preset names calendar days in the viewer's time zone. The RPCs take
// instants, so a window runs from the first day's local midnight to the local
// midnight AFTER its last day (end exclusive). Calendar arithmetic goes through
// date-fns so a window that crosses a DST change stays whole days — the old
// millisecond arithmetic drifted by an hour.

import { addDays, differenceInCalendarDays, subDays, subYears } from 'date-fns';
import { getLast12MonthsPreset, type CompareMode, type DatePreset } from '../constants';
import type { AnalyticsWindow, DateRange } from './types';

/** 'YYYY-MM-DD' as that day's local midnight. */
export function localDay(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function presetBounds(preset: DatePreset): { start: Date; end: Date } {
  return { start: localDay(preset.startDate), end: addDays(localDay(preset.endDate), 1) };
}

/** The window a comparison is made against, or null for no comparison. */
export function comparisonBounds(
  start: Date,
  end: Date,
  mode: CompareMode,
): { start: Date; end: Date } | null {
  if (mode === 'none') return null;
  if (mode === 'same_period_last_year') return { start: subYears(start, 1), end: subYears(end, 1) };
  const days = differenceInCalendarDays(end, start);
  return { start: subDays(start, days), end: start };
}

const toRange = (b: { start: Date; end: Date }): DateRange => ({
  start: b.start.toISOString(),
  end: b.end.toISOString(),
});

export function analyticsWindow(preset: DatePreset, mode: CompareMode): AnalyticsWindow {
  const bounds = presetBounds(preset);
  const previous = comparisonBounds(bounds.start, bounds.end, mode);
  return { current: toRange(bounds), previous: previous ? toRange(previous) : null };
}

export const DEFAULT_COMPARE_MODE: CompareMode = 'previous_period';

/** What the page opens on — shared with the sidebar's prefetch so both build
 *  the same query keys. */
export function defaultAnalyticsWindow(): AnalyticsWindow {
  return analyticsWindow(getLast12MonthsPreset(), DEFAULT_COMPARE_MODE);
}
